import { useState, useEffect } from 'react';
import { useNavigation } from "../context/useNavigation";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import Statcard from "../components/Statcard";
import DashboardHighlights from "../components/DashboardHighlights";
import LiveRideMonitor from "../components/LiveRideMonitor";
import FleetPerformance from "../components/FleetPerformance";
import PaymentChart from "../components/PaymentChart";
import Complaints from "../components/Complaints";
import DispatchPanel from "../components/DispatchPanel";
import RideTimelineModal from "../components/RideTimelineModal";
import { X } from "lucide-react";
import { createPortal } from "react-dom";
import Drivers from "./Drivers";
import api, { getAllPages, logError } from "../services/api";
import { formatNaira } from "../utils/money";
import { startOfWatDay } from "../utils/tripLog";

// A missing count shows "—"; only a real 0 shows 0.
const formatCount = (value) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—';

// Placeholder components for other views
const RidersView = () => <div className="p-6">Rider Management View</div>;
const TripsView = () => <div className="p-6">Trip Logs View</div>;
const PaymentsView = () => <div className="p-6">Payment & Earnings View</div>;
const FleetView = () => <div className="p-6">Fleet Management View</div>;
const SettingsView = () => <div className="p-6">Settings View</div>;

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_BOOKING_PAGES = 50;

// Bookings from the last 30 days only (newest first): enough for "Cancelled
// today" and the fleet chart. All-time counts come from /admin/highlights.
const loadRecentBookings = async () => {
  const since = Date.now() - THIRTY_DAYS_MS;
  const rows = [];
  for (let page = 1; page <= MAX_BOOKING_PAGES; page += 1) {
    const { data } = await api.get('/v1/admin/bookings', { params: { page, limit: 100 } });
    const list = Array.isArray(data?.data) ? data.data : [];
    rows.push(...list.filter((b) => new Date(b.createdAt).getTime() >= since));
    const reachedOld = list.some((b) => new Date(b.createdAt).getTime() < since);
    const lastPage = list.length === 0 || page >= (data?.pagination?.totalPages || 1);
    if (reachedOld || lastPage) return rows;
  }
  throw new Error(`Stopped after ${MAX_BOOKING_PAGES} pages; the list is incomplete.`);
};

// Successful payouts: the server-side total first, then the dashboard aggregate.
const successfulPayoutsNgn = (financial, dashboard) => {
  const fromSummary = financial?.payouts?.totalSentToDrivers?.ngn;
  if (typeof fromSummary === 'number') return fromSummary;
  const row = (dashboard?.withdrawals?.byStatus || []).find((r) => r._id === 'success');
  return typeof row?.totalAmountKobo === 'number' ? row.totalAmountKobo / 100 : null;
};

function Dashboard() {
  const { activeItem } = useNavigation();

  // Each source loads on its own, so one failure only marks its own tile.
  const [money, setMoney] = useState({ commissionNgn: null, passengersPaidNgn: null, loading: true, error: null });
  const [online, setOnline] = useState({ count: null, loading: true, error: null });
  const [completed, setCompleted] = useState({ count: null, loading: true, error: null });
  const [trips, setTrips] = useState({ cancelledToday: null, recent: [], loading: true, error: null });
  const [analytics, setAnalytics] = useState({ revenue: null, revenueByMonth: [], withdrawals: { totalAmount: null, allWithdrawals: [], barsError: false } });

  // The ride picked in the live monitor; the dispatch panel acts on it.
  const [dispatchBookingId, setDispatchBookingId] = useState(null);
  const [waitingCount, setWaitingCount] = useState(0);
  // Phones have no room for the side panel: a waiting ride opens a dispatch
  // sheet, any other ride opens its timeline.
  const [phoneSheet, setPhoneSheet] = useState(null); // { id, waiting }
  const isLarge = () => window.matchMedia("(min-width: 1024px)").matches;
  const selectRide = (id, row) => {
    setDispatchBookingId(id);
    if (!isLarge()) setPhoneSheet({ id, waiting: row?.status === "requested" });
  };

  // Bumped by Retry to re-run the dashboard fetch.
  const [reloadKey, setReloadKey] = useState(0);
  const retry = () => setReloadKey((k) => k + 1);

  useEffect(() => {
    if (activeItem !== 'dashboard') return undefined;
    let alive = true;

    const loadSummary = async () => {
      const [dashboardRes, analyticsRes, financialRes, highlightsRes] = await Promise.allSettled([
        api.get('/v1/admin/dashboard'),
        api.get('/v1/admin/analytics'),
        api.get('/v1/admin/analytics/financial-summary'),
        api.get('/v1/admin/highlights'),
      ]);
      if (!alive) return;
      const ok = (res) => (res.status === 'fulfilled' ? res.value.data : null);
      const dashboard = ok(dashboardRes);
      const analyticsData = ok(analyticsRes);
      const financial = ok(financialRes);
      const highlights = ok(highlightsRes);

      const commissionNgn = financial?.profit?.grossProfit?.ngn ?? analyticsData?.revenue?.totalCommissionNgn ?? null;
      setMoney({
        commissionNgn,
        passengersPaidNgn: financial?.cashflow?.collected?.chargedForRides?.naira ?? null,
        loading: false,
        error: commissionNgn === null && (financialRes.status === 'rejected' || analyticsRes.status === 'rejected')
          ? 'failed' : null,
      });

      // Server counts: riders with isAvailable, and bookings with status completed.
      const onlineCount = highlights?.riders?.online;
      setOnline({
        count: typeof onlineCount === 'number' ? onlineCount : null,
        loading: false,
        error: typeof onlineCount === 'number' ? null : 'failed',
      });
      const completedCount = highlights?.rides?.completed;
      setCompleted({
        count: typeof completedCount === 'number' ? completedCount : null,
        loading: false,
        error: typeof completedCount === 'number' ? null : 'failed',
      });

      setAnalytics((prev) => ({
        ...prev,
        revenue: analyticsData?.revenue
          ? {
              totalFareNgn: analyticsData.revenue.totalFareNgn ?? null,
              totalCommissionNgn: analyticsData.revenue.totalCommissionNgn ?? null,
              totalRides: analyticsData.revenue.totalRides ?? null,
            }
          : null,
        revenueByMonth: analyticsData?.revenueByMonth || [],
        withdrawals: { ...prev.withdrawals, totalAmount: successfulPayoutsNgn(financial, dashboard) },
      }));
    };

    const loadTrips = async () => {
      try {
        const recent = await loadRecentBookings();
        if (!alive) return;
        const todayStart = startOfWatDay();
        setTrips({
          cancelledToday: recent.filter(
            (b) => b.status === 'cancelled' && new Date(b.createdAt) >= todayStart,
          ).length,
          recent,
          loading: false,
          error: null,
        });
      } catch {
        if (alive) setTrips((prev) => ({ ...prev, loading: false, error: 'failed' }));
      }
    };

    // The monthly withdrawal bars need every successful payout; loaded once, not on the poll.
    const loadWithdrawals = async () => {
      try {
        const rows = await getAllPages('/v1/admin/withdrawals', { status: 'success' });
        if (alive) setAnalytics((prev) => ({ ...prev, withdrawals: { ...prev.withdrawals, allWithdrawals: rows, barsError: false } }));
      } catch (error) {
        logError('Error fetching withdrawals for the chart:', error);
        if (alive) setAnalytics((prev) => ({ ...prev, withdrawals: { ...prev.withdrawals, barsError: true } }));
      }
    };

    // One refresh at a time; a slow cycle is skipped, not stacked.
    let running = false;
    const refresh = async () => {
      if (running) return;
      running = true;
      try {
        await Promise.all([loadSummary(), loadTrips()]);
      } finally {
        running = false;
      }
    };

    refresh();
    loadWithdrawals();
    // Poll so the dashboard stays close to real time; paused while the tab is hidden.
    const intervalId = setInterval(() => {
      if (!document.hidden) refresh();
    }, 30000);
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      alive = false;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [activeItem, reloadKey]);

  const renderContent = () => {
    switch (activeItem) {
      case "dashboard":
        return (
          <div className="space-y-6">
            {/* Financial / fleet snapshot */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Statcard
                title="Zooma commission"
                value={formatNaira(money.commissionNgn)}
                sub={`Passengers paid ${formatNaira(money.passengersPaidNgn)}`}
                description="Zooma's own cut of every ride. Passengers paid is what customers were charged for rides; most of it goes to riders."
                className="bg-[#E9D9EF] dark:text-black"
                loading={money.loading}
                error={money.error}
                onRetry={retry}
              />
              <Statcard
                title="Keke Online"
                value={formatCount(online.count)}
                description="Riders with Online switched on right now."
                className="bg-[#FFD36980] dark:text-black"
                loading={online.loading}
                error={online.error}
                onRetry={retry}
              />
              <Statcard
                title="Completed rides"
                value={formatCount(completed.count)}
                sub="All time"
                description="Trips that finished. Cancelled and expired requests are not counted."
                className="bg-[#DFF0E8] text-[#0E9F6E] dark:text-black"
                loading={completed.loading}
                error={completed.error}
                onRetry={retry}
              />
              <Statcard
                title="Cancelled today"
                value={formatCount(trips.cancelledToday)}
                description="Ride requests made today (Lagos time) that were cancelled or expired."
                className="bg-[#FFFBEB] text-[#F59E0B] dark:text-black"
                loading={trips.loading}
                error={trips.error}
                onRetry={retry}
              />
            </div>

            {/* Live ride monitor + dispatch: pick a ride on the left, nudge or
                assign it on the right. */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              <div className="lg:col-span-7">
                <LiveRideMonitor
                  selectedId={dispatchBookingId}
                  onSelect={selectRide}
                  onWaitingCount={setWaitingCount}
                />
              </div>
              <div className="hidden lg:block lg:col-span-5">
                <DispatchPanel bookingId={dispatchBookingId} waitingCount={waitingCount} />
              </div>
            </div>
            {/* Portalled to <body>: an ancestor here clips position:fixed. */}
            {phoneSheet?.waiting ? createPortal(
              <div className="fixed inset-0 z-40 flex flex-col bg-white dark:bg-gray-900 lg:hidden" role="dialog" aria-modal="true" aria-label="Dispatch">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Ride waiting for a rider</span>
                  <button
                    type="button"
                    onClick={() => setPhoneSheet(null)}
                    className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 min-h-0">
                  <DispatchPanel bookingId={phoneSheet.id} waitingCount={waitingCount} fill />
                </div>
              </div>,
              document.body,
            ) : phoneSheet ? (
              <RideTimelineModal bookingId={phoneSheet.id} onClose={() => setPhoneSheet(null)} />
            ) : null}

            {/* Data highlights — onboarding, ride volume, pickups, ratings */}
            <DashboardHighlights />

            {/* Earnings chart + complaints */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
                <PaymentChart analytics={analytics} />
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4 flex flex-col">
                <Complaints className="h-full" />
              </div>
            </div>

            {/* Fleet performance */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
              <FleetPerformance
                bookings={trips.recent}
                loading={trips.loading}
                error={trips.error}
                onRetry={retry}
              />
            </div>
          </div>
        );
      case "drivers":
        return <Drivers />;
      case "riders":
        return <RidersView />;
      case "trips":
        return <TripsView />;
      case "payments":
        return <PaymentsView />;
      case "fleet":
        return <FleetView />;
      case "settings":
        return <SettingsView />;
      default:
        return (
          <div className="p-6">
            <h1 className="text-2xl font-bold">Welcome to Zooma Admin</h1>
          </div>
        );
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default Dashboard;